import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import {
  AppContext,
  DeleteModal,
  Icon,
  SearchFilter,
  getTranslatedText,
  BackgroundIcon,
  toast,
  AccessControl,
  isAuthorised,
  EventEmitter,
  ListInfoBanner,
  useUserMSPCheckHook,
  SortComponent,
  FeatureGuide,
  useActivityTracker,
  COMMON_CONSTANTS,
  ALL_TABLES,
  useSuperListHook,
  ResetFilter,
  TICKET_SOURCE,
  AssociateButton
} from '@nile/components';
import {
  GET_ENTITY_PROPERTIES,
  GET_STATUS_CONFIGURATION,
  GET_TICKET_LISTVIEW_META,
  SAVE_PERSONALIZATION,
  GET_PERSONALIZATION_BY_NAME,
  AccountClient,
  CONVERT_UNKNOWN_EMAIL_TO_TICKET_NEW,
  GET_TICKET_META_FOR_BULK_UPDATE,
  BATCH_UPDATE_TICKETS
} from '@nile/graphql';
import { DEFAULT_TICKET_CONDITION } from '@nile/components/src/Pebbles/AdvancedTicketFilter';
import { useFlags, withLDConsumer } from 'launchdarkly-react-client-sdk';
import {
  DeleteConfirmation,
  MergeTicketSheet
} from '@nile/mangifera/src/Components/Sheets';
import { cloneDeep, isEmpty } from 'lodash';
import AdvancedFilter from '@nile/components/src/Components/AdvancedFilter';
import AdvancedTicketFilter from '@nile/mangifera/src/Components/Ticket/AdvancedTicketFilter';
import ListViewColumns from '@nile/mangifera/src/Containers/SheetContainers/ListViewColumns';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { DELETE_TICKET_VIEW } from '@nile/mangifera/src/Graphql/Mutations/ticketview';
import {
  TICKET_VIEW_IDS,
  sortActionsTicketKanbanMap
} from '@nile/mangifera/src/Constants/Tickets/index';
import {
  Badge,
  Button,
  ButtonGroup,
  Heading,
  ResourceItem,
  Tag,
  Popover
} from '@superops/ui-design-system';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Tooltip } from 'react-clone-tippy';
import 'react-clone-tippy/dist/tippy.css';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import BulkClosureModal from './BulkClosureModal';
import UnspamTicketButton from '@nile/mangifera/src/Components/Ticket/UnspamTicketButton.tsx';
import BulkUpdatePropertyModal from './BulkUpdatePropertyModal';
import useClassicViewHook from '@nile/components/src/Hooks/useClassicViewHook';
import ImportButton from '@nile/mangifera/src/Components/ImportButton/ImportButton';

const refreshCounts = (viewIds) => {
  EventEmitter.dispatch('refreshTicketViewCount', viewIds);
};

function TicketActionToolbar(props) {
  const {
    intl,
    context,
    activeView,
    appContext,
    setBulkErrorsOnClosure,
    setModalActive,
    viewId,
    bulkUpdatePropModalActive,
    setBulkUpdatePropModalActive,
    isClassicView,
    selectedRecordsHook,
    updateRecords,
    getTicketsIds
  } = props;
  const {
    records,
    mergeTickets,
    softDeleteTickets,
    markSpamTickets,
    updateTickets,
    restoreTickets,
    hardDeleteTickets,
    mergeTicketLoading,
    softDeleteLoading,
    spamming,
    restoring,
    hardDeleteLoading
  } = context;

  const flags = useFlags();

  const {
    records: superListRecords,
    setRecords: setSuperListRecords
  } = useSuperListHook({
    tableName: ALL_TABLES.tickets,
    viewId
  });

  const selectedRecords = isClassicView
    ? selectedRecordsHook
    : superListRecords || [];

  const setSelectedRecords = isClassicView
    ? updateRecords
    : setSuperListRecords;

  const [selected, setSelected] = useState(null);
  const { data: statusConfig, loading, error } = useQuery(
    GET_STATUS_CONFIGURATION
  );

  const isSpamView = activeView?.id === TICKET_VIEW_IDS.spam;

  const selectTicket = useCallback((value) => {
    setSelected(value);
  }, []);

  const [resolveLoading, setResolveLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);

  const assignToMe = () => {
    setAssignLoading(true);
    const { user } = appContext;
    // let selectedTickets = context.records;
    const selectedTicketIds = getTicketsIds('assign');
    const selectedTickets = selectedTicketIds.map((ticketId) => {
      return {
        ticketId: ticketId,
        technician: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          userId: user.userId
        }
      };
    });
    updateTickets({
      variables: { ticketInput: selectedTickets },
      input: selectedTickets
    }).then(() => {
      EventEmitter.dispatch('refetchClassicViewList');
      setAssignLoading(false);
      setSelectedRecords([]);
    });
  };

  const getClosureFields = (errorFields) => {
    const closureErrors = errorFields.filter((err) => err?.errorCode === 10012);
    setBulkErrorsOnClosure(closureErrors);
    setModalActive(true);
  };

  useEffect(() => {
    EventEmitter.subscribe('bulkClosureError', getClosureFields);
    return function cleanup() {
      EventEmitter.unsubscribe('bulkClosureError');
    };
  });

  const resolveTickets = () => {
    setResolveLoading(true);
    // let selectedTickets = context.records;
    const selectedTicketIds = getTicketsIds('resolve');
    let status =
      statusConfig.getStatusConfiguration?.find(
        (el) => el.configuration === 'RESOLVE'
      )?.status.name || null;
    const selectedTickets = selectedTicketIds.map((ticketId) => {
      return {
        ticketId: ticketId,
        status: status
      };
    });
    updateTickets({
      variables: { ticketInput: selectedTickets },
      input: selectedTickets
    })
      .then((res) => {
        setResolveLoading(false);
        EventEmitter.dispatch('refetchClassicViewList');
        setSelectedRecords([]);
      })
      .catch((err) => {
        setResolveLoading(false);
      });
  };

  const merge = (callback, variables) => {
    const parentId = selected?.[0] || selectedRecords?.[0]?.ticketId;
    const selectedTicketIds = getTicketsIds('merge');
    const childIds = selectedTicketIds.reduce(function(filtered, ticketId) {
      if (ticketId !== parentId) {
        filtered.push(ticketId);
      }
      return filtered;
    }, []);
    mergeTickets({
      variables: {
        input: {
          parentId: parentId,
          childIds: childIds,
          ...variables
        }
      }
    }).then((res) => {
      callback && callback();
      refreshCounts([activeView.id]);
      EventEmitter.dispatch('refetchClassicViewList');
      setSelectedRecords([]);
    });
  };

  const deleteTicket = () => {
    var ids = getTicketsIds('delete');
    softDeleteTickets({
      variables: {
        ids: ids
      }
    }).then(() => {
      refreshCounts([activeView.id, TICKET_VIEW_IDS.trash]);
      EventEmitter.dispatch('refetchClassicViewList');
      setSelectedRecords([]);
    });
  };

  const markTicketAsSpam = () => {
    var ids = getTicketsIds('spam');
    markSpamTickets({
      variables: {
        ids: ids,
        markSpamRequester: true
      }
    }).then(() => {
      refreshCounts([activeView.id, TICKET_VIEW_IDS.spam]);
      EventEmitter.dispatch('refetchClassicViewList');
      setSelectedRecords([]);
    });
  };

  const onUnspamCompleted = () => {
    refreshCounts([activeView.id, TICKET_VIEW_IDS.spam]);
    EventEmitter.dispatch('refetchClassicViewList');
    setSelectedRecords([]);
  };

  const getUnspamTicketVariablesInput = () => {
    const ids = getTicketsIds('unspam');
    const ticketIds = ids.map((el) => ({ ticketId: el }));
    return {
      ticket: ticketIds,
      unspamRequester: true
    };
  };

  const getSelectedItems = () => {
    return selectedRecords.map((rec) => rec.ticketId);
  };

  const handleRestore = () => {
    restoreTickets({ variables: { ids: getSelectedItems() } });
  };

  const handlePermanentDelete = () => {
    hardDeleteTickets({ variables: { ids: getSelectedItems() } }).then(() => {
      refreshCounts([activeView.id]);
    });
  };

  const bulkUpdatePropertyHandler = () => {
    setBulkUpdatePropModalActive(true);
  };

  const [
    convertUnknownEmaiklToTicket,
    { loading: convertTicketLoading }
  ] = useMutation(CONVERT_UNKNOWN_EMAIL_TO_TICKET_NEW, {
    onCompleted(res) {
      EventEmitter.dispatch('refreshList');
      setSelectedRecords([]);
    }
  });

  const onSubmitBulkAssociate = (clientData, callback) => {
    const selectedTicketIds = getTicketsIds();

    convertUnknownEmaiklToTicket({
      variables: {
        input: {
          ...clientData,
          tickets: selectedTicketIds.map((ticketId) => ({ ticketId }))
        }
      }
    }).then((res) => {
      if (res) {
        toast(
          'success',
          getTranslatedText(intl, 'ticket.unknown.association.success')
        );
        if (callback) callback();
      }
    });
  };

  const associateButton = () => {
    return (
      <AssociateButton
        // loading={loading}
        // meta={metaData}
        // selectedClient={selectedClient}
        // setSelectedClient={setSelectedClient}
        onSubmit={onSubmitBulkAssociate}
        submitLoading={convertTicketLoading}
        showButtonText
        isBulkUpdate
      />
    );
  };

  if (activeView?.systemViewProperty === 'TRASH_TICKET') {
    return (
      <div className="flex actr full-width">
        <div className="flex f11">
          <div>
            <Heading element="h1" weight="Medium">
              <div className="flex center">
                <div className="flex center">
                  <div className="mr-20" onClick={props.toggleNavMenu}>
                    {props.navMenu}
                  </div>
                  {activeView.name}
                  {activeView.shared ? (
                    <Tooltip
                      title={getTranslatedText(intl, 'common.shared.view')}
                      delay={200}
                      duration={250}
                      position="bottom"
                      inertia
                      trigger="mouseenter"
                      animation="shift"
                      animateFill={false}
                    >
                      <div className="fx center mr8 ml-16">
                        <SVGIcon
                          name="GlobeSharedView"
                          size="md"
                          color="#6e7175"
                        />
                      </div>
                    </Tooltip>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </Heading>
          </div>
        </div>
        <div className="flex margin-auto f11">
          <ListInfoBanner message="ticket.view.trash.information" />
        </div>
        <div className="flex f11">
          <div className=" mlauto">
            <ButtonGroup>
              <Tag selected secondary>
                {`${selectedRecords?.length} ${getTranslatedText(
                  intl,
                  'ticket.details.selected'
                )}`}
              </Tag>
              <Button
                onClick={handleRestore}
                id="restoreBtn"
                icon={<SVGIcon name="Restore" size="sm" color="#323232" />}
                loading={restoring}
              >
                <span className="fx actr">
                  <FormattedMessage id="client.view.restore.button" />
                </span>
              </Button>
              <Button
                onClick={handlePermanentDelete}
                id="deleteBtn"
                icon={<SVGIcon name="Delete" size="sm" />}
                loading={hardDeleteLoading}
              >
                <span className="fx actr danger">
                  <FormattedMessage id="client.view.delete.button" />
                </span>
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
    );
  }

  if (activeView?.systemViewProperty === 'UNKNOWN_TICKET') {
    return (
      <div className="flex full-width">
        <div className="mlauto">
          <ButtonGroup>
            <Tag secondary selected>
              {`${selectedRecords?.length} ${getTranslatedText(
                intl,
                'ticket.details.selected'
              )}`}
            </Tag>
            <AccessControl
              roleToCheck="tickets.edit"
              render={associateButton()}
            />

            <AccessControl
              roleToCheck="tickets.delete"
              render={
                <DeleteConfirmation
                  confirm={deleteTicket}
                  loading={softDeleteLoading}
                />
              }
            />
          </ButtonGroup>
        </div>
      </div>
    );
  }

  return (
    <div className="mlauto">
      <ButtonGroup>
        <Tag secondary selected>
          {`${selectedRecords?.length} ${getTranslatedText(
            intl,
            'ticket.details.selected'
          )}`}
        </Tag>
        <AccessControl
          roleToCheck="tickets.edit"
          render={
            <Button
              onClick={assignToMe}
              icon={<SVGIcon name="AssignToMeIcon" size="sm" />}
              loading={assignLoading}
            >
              <FormattedMessage id="ticket.details.assign" />
            </Button>
          }
        />
        <AccessControl
          roleToCheck="tickets.edit"
          render={
            <Button
              onClick={resolveTickets}
              icon={<SVGIcon name="Resolution" />}
              loading={resolveLoading}
            >
              <FormattedMessage id="ticket.details.resolve" />
            </Button>
          }
        />

        <AccessControl
          roleToCheck="mergeTicket.allow"
          render={
            <MergeTicketSheet
              records={selectedRecords}
              selectTicket={selectTicket}
              selected={selected || [selectedRecords[0].ticketId]}
              merge={merge}
              loading={mergeTicketLoading}
            />
          }
        />

        <AccessControl
          roleToCheck="tickets.delete"
          render={
            <DeleteConfirmation
              confirm={deleteTicket}
              loading={softDeleteLoading}
            />
          }
        />

        <AccessControl
          roleToCheck="spamTicket.allow"
          render={
            <>
              {isSpamView ? (
                <UnspamTicketButton
                  inputVariables={getUnspamTicketVariablesInput()}
                  onCompleted={onUnspamCompleted}
                  showWarning
                />
              ) : (
                <Button
                  onClick={markTicketAsSpam}
                  icon={<SVGIcon name="Spam" color="#323232" />}
                  loading={spamming}
                >
                  <FormattedMessage id="ticket.details.spam" />
                </Button>
              )}
            </>
          }
        />
        <AccessControl
          roleToCheck="tickets.edit"
          render={
            <Button
              onClick={bulkUpdatePropertyHandler}
              icon={<SVGIcon name="Edit" />}
            >
              <FormattedMessage id="app.btn.editfield" />
            </Button>
          }
        />
      </ButtonGroup>
    </div>
  );
}

function TicketInfoToolbar(props) {
  const {
    intl,
    context,
    appContext,
    activeView,
    params,
    flags,
    viewId,
    isClassicView,
    selectedRecordsHook,
    updateRecords
  } = props;

  const {
    records: superListRecords,
    setRecords: setSuperListRecords,
    updateAdvanceFilterCondition,
    advanceFilterCondition
  } = useSuperListHook({
    tableName: ALL_TABLES.tickets,
    viewId
  });

  const selectedRecords = isClassicView
    ? selectedRecordsHook
    : superListRecords || [];

  const setSelectedRecords = isClassicView
    ? updateRecords
    : setSuperListRecords;

  const [popoverActive, setPopoverActive] = useState(false);
  const [advancedConditions, setAdvancedConditions] = useState(null);
  const [active, setActive] = useState(false);
  const [kanbanSort, setKanbanSort] = useState({});
  const { user } = appContext;
  const isMSP = useUserMSPCheckHook();
  const { viewType, setViewType, activeKanbanTab } = context;
  const [deleteTicketView] = useMutation(DELETE_TICKET_VIEW, {
    onCompleted({ deleteSLA }) {
      toast('success', 'app.toast.ticket.delete.success');
    },
    update(cache, { data }) {},
    onError() {
      // toast('error', 'Something went wrong');
    }
  });
  const deleteView = (id) => {
    deleteTicketView({
      variables: { id }
    }).then((res) => {
      if (res?.data?.deleteTicketView) {
        EventEmitter.dispatch('deleteTicketView');
      }
    });
  };

  function getColumnPersonalization() {
    AccountClient.query({
      query: GET_PERSONALIZATION_BY_NAME,
      fetchPolicy: 'network-only',
      variables: {
        name: activeView?.id,
        category: 'TicketKanbanViewPersonalization',
        userId: user?.userId
      }
    }).then((res) => {
      const kanbanData = res.data.getPersonalizationByCategoryAndName
        ? JSON.parse(res.data.getPersonalizationByCategoryAndName?.value)
        : {};
      setKanbanSort(kanbanData?.[activeKanbanTab]);
    });
  }

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((popoverActive) => !popoverActive);
  });

  const activator = (
    <div className="ml8">
      <Button
        onClick={togglePopoverActive}
        plain
        monochrome
        icon={<Badge radial icon={<SVGIcon name="Actions" size="md" />} />}
      />
    </div>
  );

  useEffect(() => {
    if (context?.previousView?.id !== activeView?.id) {
      context.setViewConditions(null);
      if (viewType === 'kanban') {
        getColumnPersonalization();
      }
    }
  }, [activeView?.id, activeKanbanTab]);

  useEffect(() => {
    if (viewType === 'kanban') {
      getColumnPersonalization();
    }
  }, []);

  const updateAdvancedConditions = (condition = []) => {
    let complexCondition = cloneDeep(context.viewConditions);
    const listCondition =
      complexCondition?.operands?.[0] || DEFAULT_TICKET_CONDITION;
    complexCondition = {
      joinOperator: 'AND',
      operands: [listCondition].concat(condition || [])
    };

    context.setViewConditions(complexCondition);
  };

  const modalActivator = (
    <div className="pointer flex center">
      <span className="mr10">
        <SVGIcon name="Delete" />
      </span>
      <FormattedMessage id="common.delete" />
    </div>
  );

  const handleSortAction = (sortData) => {
    EventEmitter.dispatch('kanbanSort', sortData);
    // getColumnPersonalization();
    setKanbanSort(sortData);
  };

  const getSortTitle = () => {
    const sortValue = sortActionsTicketKanbanMap.find(
      (action) => action.value === kanbanSort?.attribute
    );
    if (sortValue) {
      return sortValue.label;
    }
    return 'kanbanview.actionlist.createdTime';
  };

  const userRole = appContext?.user?.role?.roleFeatureList || [];
  const isDeleteEditAuthorised =
    isAuthorised(userRole, 'ticketViews.edit') ||
    isAuthorised(userRole, 'ticketViews.delete');

  const getViewTypes = () => {
    return (
      <div className="flex center jend mlauto g10">
        {!isClassicView && (
          <ResetFilter
            tableName={ALL_TABLES.tickets}
            viewId={viewId}
            className="mr10 ash-kanban"
            showOnlyOnAdvancedFilter={viewType !== 'list'}
          />
        )}
        <ImportButton
          module="tickets"
          onImportComplete={() => {
            EventEmitter.dispatch('refreshList');
          }}
          onIntegrationClick={(route) => {
            props.history.push(route);
          }}
        />
        <ButtonGroup>
          {viewType === 'kanban' && (
            <Tooltip
              title={getTranslatedText(intl, 'communityScript.sort')}
              delay={200}
              duration={250}
              position="bottom"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <SortComponent
                title={getSortTitle()}
                currentSortOrder={kanbanSort?.order}
                currentSortAttribute={kanbanSort?.attribute}
                defaultValue={sortActionsTicketKanbanMap[0]}
                handleSortAction={handleSortAction}
                sortActionsList={sortActionsTicketKanbanMap.filter((action) => {
                  const val = action?.value?.split('.')[0];
                  return val !== activeKanbanTab;
                })}
              />
            </Tooltip>
          )}
          <div>
            {isMSP && (
              <ButtonGroup segmented>
                <Tooltip
                  title={getTranslatedText(intl, 'ticket.listView.tooltip')}
                  delay={200}
                  duration={250}
                  position="bottom"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <Button
                    outline={viewType === 'list'}
                    onClick={() => setViewType('list')}
                    icon={<SVGIcon name="ListView" size="sm" />}
                  />
                </Tooltip>
                {activeView?.systemViewProperty !== 'TRASH_TICKET' && (
                  <Tooltip
                    title={getTranslatedText(intl, 'ticket.kanbanView.tooltip')}
                    delay={200}
                    duration={250}
                    position="bottom"
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                  >
                    <Button
                      outline={viewType === 'kanban'}
                      highlighted={viewType === 'kanban'}
                      onClick={() => {
                        setViewType('kanban');
                        props.closeNavMenu();
                      }}
                      icon={<SVGIcon size="sm" name="KanbanView" />}
                    />
                  </Tooltip>
                )}
                <Tooltip
                  title={getTranslatedText(intl, 'ticket.classicView.tooltip')}
                  delay={200}
                  duration={250}
                  position="bottom"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <Button
                    outline={viewType === 'classic'}
                    highlighted={viewType === 'classic'}
                    onClick={() => {
                      setViewType('classic');
                      props.closeNavMenu();
                      useActivityTracker({
                        event: 'Ticket - classic view'
                      });
                    }}
                    icon={<SVGIcon size="md" name="ClassicView" />}
                  />
                </Tooltip>
              </ButtonGroup>
            )}
          </div>
          {appContext?.accountType === 'MSP' && (
            <>
              {viewType === 'list' && (
                <TicketListColumns
                  intl={intl}
                  location={props.location}
                  activeView={activeView}
                />
              )}
            </>
          )}
          {appContext?.accountType === 'MSP' && viewType !== 'classic' && (
            <AdvancedTicketFilter
              updateCondition={updateAdvanceFilterCondition}
              conditions={advanceFilterCondition}
            />
          )}
          {activeView?.systemViewProperty !== 'UNKNOWN_TICKET' &&
            viewType !== 'classic' && (
              <AccessControl
                roleToCheck="tickets.add"
                render={
                  <div>
                    <Button
                      primary
                      id="createTicket"
                      onClick={() => {
                        context.openTab({
                          module: 'NEW',
                          id: 'new-tab'
                        });
                      }}
                      icon={<SVGIcon name="Add" color="#fff" />}
                    >
                      <FormattedMessage id="common.create" />
                    </Button>
                  </div>
                }
              />
            )}
        </ButtonGroup>
      </div>
    );
  };

  const renderViewStates = () => {
    if (selectedRecords?.length === 0) {
      if (activeView?.systemViewProperty === 'TRASH_TICKET') {
        return (
          <>
            <div className="flex jend f11">
              <ListInfoBanner message="ticket.view.trash.information" />
            </div>
            <div className="flex f11" />
            {getViewTypes()}
          </>
        );
      }
      return getViewTypes();
    }
    return null;
  };

  return (
    <div
      className={`flex ${selectedRecords?.length === 0 ? 'full-width' : ''}`}
    >
      <div className="flex f11">
        <Heading element="h1" weight="Medium">
          <div className="flex center">
            <div className="flex center">
              <div className="mr-20" onClick={props.toggleNavMenu}>
                {props.navMenu}
              </div>
              {activeView.name}
              {activeView.shared ? (
                <Tooltip
                  title={getTranslatedText(intl, 'common.shared.view')}
                  delay={200}
                  duration={250}
                  position="bottom"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <div className="fx center mr8 ml-16">
                    <SVGIcon name="GlobeSharedView" size="md" color="#6e7175" />
                  </div>
                </Tooltip>
              ) : (
                ''
              )}
            </div>
            {activeView.customView && isDeleteEditAuthorised && (
              <Popover
                active={popoverActive}
                activator={activator}
                preferredAlignment="left"
                onClose={() => {
                  togglePopoverActive();
                }}
              >
                <div className="min-width-150 flex col no-scroll">
                  <AccessControl
                    roleToCheck="ticketViews.edit"
                    render={
                      <ResourceItem
                        verticalAlignment="center"
                        gutter="md"
                        onClick={() => {
                          context.openTab({
                            module: 'TICKET_VIEW',
                            data: {
                              id: params.id,
                              value: activeView.name,
                              type: 'edit'
                            }
                          });
                        }}
                      >
                        <div className="flex actr g5">
                          <SVGIcon name="Edit" />
                          <FormattedMessage id="ticket.test.dropDown.modify" />
                        </div>
                      </ResourceItem>
                    }
                  />

                  <AccessControl
                    roleToCheck="ticketViews.delete"
                    render={
                      <ResourceItem verticalAlignment="center" gutter="md">
                        <DeleteModal
                          modalActivator={modalActivator}
                          description={getTranslatedText(
                            intl,
                            'ticket.test.dropDown.delete.confirm'
                          )}
                          handleDeleteConfirmation={() =>
                            deleteView(activeView.id)
                          }
                          title={getTranslatedText(
                            intl,
                            'ticket.test.dropDown.delete.text'
                          )}
                        />
                      </ResourceItem>
                    }
                  />
                </div>
              </Popover>
            )}
          </div>
        </Heading>
      </div>
      {activeView?.systemViewProperty !== 'TRASH_TICKET' &&
        selectedRecords?.length === 0 &&
        isMSP && (
          <div className="mr-10 fx center">
            <FeatureGuide featureName="ticketlistandkanban" video onlyIcon />
          </div>
        )}
      {renderViewStates()}
    </div>
  );
}

function TicketListColumns(props) {
  const { intl, activeView } = props;
  const {
    loading: entitiesLoading,
    error: entitiesError,
    data: allEntities
  } = useQuery(GET_ENTITY_PROPERTIES, { client: AccountClient });
  const {
    loading: ticketListViewMetaLoading,
    error: ticketListViewMetaError,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });
  const context = useContext(TicketContext);
  const appContext = useContext(AppContext);
  const customColumns = context.personalization
    ? typeof context.personalization === 'string'
      ? JSON.parse(context.personalization)
      : context.personalization
    : [];
  const [columns, setColumns] = useState(customColumns);
  const [savePersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient,
    onCompleted({ savePersonalization }) {
      if (savePersonalization) {
        // eslint-disable-line
        EventEmitter.dispatch('refreshList');
      }
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });
  useEffect(() => {
    if (context.personalization) {
      setColumns(
        typeof context.personalization === 'string'
          ? JSON.parse(context.personalization)
          : context.personalization
      );
    }
  }, [context.personalization]);
  const [previousLocation, setPreviousLocation] = useState('');
  useEffect(() => {
    setPreviousLocation(props.location.pathname);
    return function cleanup() {};
  }, []);

  useEffect(() => {
    if (
      previousLocation !== props?.location?.pathname &&
      context?.previousView?.id &&
      context?.previousView?.id !== activeView?.id
    ) {
      setPreviousLocation(props.location.pathname);
      context.setPersonalization('');
    }
  }, [props.location?.pathname]);
  if (entitiesLoading || ticketListViewMetaLoading)
    return <div>loading...</div>;
  if (entitiesError || ticketListViewMetaError) return <div>error...</div>;
  let sections = [];
  let fieldSection = {
    header: getTranslatedText(intl, 'ticket.columns.fields'),
    items: []
  };
  const Ticket = allEntities.getEntitiesMeta
    .find((item) => {
      return item.name === 'Ticket';
    })
    .attributes.filter((el) => {
      return (
        ticketListViewMeta?.getTicketListViewMetaInfo?.selectAttributes.indexOf(
          el.name
        ) > -1
      );
    });
  const keys = Object.keys(Ticket);
  for (let k = 0; k < keys.length; k++) {
    fieldSection.items.push({
      ...Ticket[keys[k]],
      id: Ticket[keys[k]].name,
      label: getTranslatedText(
        intl,
        Ticket[keys[k]].label || Ticket[keys[k]].name
      )
    });
  }
  sections.push(fieldSection);

  const updateParent = (selectedItems) => {
    context.setPersonalization(JSON.stringify(selectedItems));
    const currentUser = appContext?.user || {};
    const personalisedData = {
      user: {
        firstName: currentUser.firstName,
        userId: currentUser.userId,
        email: currentUser.email,
        lastName: currentUser.lastName
      },
      category: 'TicketView',
      value:
        selectedItems && selectedItems.length
          ? JSON.stringify(selectedItems)
          : ''
    };
    savePersonalization({
      variables: { ...personalisedData, name: props.activeView.id },
      input: { ...personalisedData, name: props.activeView.id }
    });
  };
  return (
    <ListViewColumns
      text={getTranslatedText(intl, 'ticket.icon.column')}
      // activator='Columns'
      columns={columns.length}
      sections={sections}
      meta={{
        heading: getTranslatedText(intl, 'ticket.columns.header'),
        selectedTitle: getTranslatedText(intl, 'ticket.columns.select')
      }}
      selectedItems={columns}
      updateParent={updateParent}
    />
  );
}

function TicketToolbar(props) {
  /*  Default active view will be all, it will served from server or constant, here it is temp fix*/
  const { intl, match, navMenu, flags, ticketViews, isClassicView } = props;

  const getActiveView = () => {
    const viewData = !isEmpty(ticketViews)
      ? ticketViews.find((item) => {
          return item.id === match.params.id;
        })
      : { name: 'All Tickets', id: '1' };
    return viewData || { name: 'All Tickets', id: '1' };
  };

  const context = useContext(TicketContext);
  const appContext = useContext(AppContext);
  const activeView = getActiveView();

  const viewId = match.params.id;

  const { records, setRecords } = useSuperListHook({
    tableName: ALL_TABLES.tickets,
    viewId
  });
  const {
    selectedRecords: selectedRecordsHook,
    updateRecords
  } = useClassicViewHook({
    module: 'ticket',
    viewId: viewId
  });

  const selectedRecords = isClassicView
    ? selectedRecordsHook || []
    : records || [];

  const setSelectedRecords = isClassicView ? updateRecords : setRecords;

  const [modalActive, setModalActive] = useState(false);
  const [bulkErrorsOnClosure, setBulkErrorsOnClosure] = useState(null);
  const bulkErrorsOnClosureByRef = React.useRef(null);
  bulkErrorsOnClosureByRef.current = bulkErrorsOnClosure;

  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [bulkUpdatePropModalActive, setBulkUpdatePropModalActive] = useState(
    false
  );
  const { updateTickets } = context;

  const BulkResolveTickets = (variables) => {
    const updatedData = variables.map((el) => el.fieldsForResponse);
    setBulkUpdateLoading(true);
    updateTickets({
      variables: { ticketInput: updatedData }
    })
      .then((res) => {
        if (res?.data) {
          setBulkErrorsOnClosure(null);
          setModalActive(false);
          setBulkUpdateLoading(false);
          EventEmitter.dispatch('refetchClassicViewList');
          setRecords([]);
          updateRecords([]);
        }
      })
      .catch((err) => setBulkUpdateLoading(false));
  };

  const [getTicketMetaForBulkUpdate, { data: bulkUpdateMeta }] = useLazyQuery(
    GET_TICKET_META_FOR_BULK_UPDATE
  );

  const [batchUpdateTickets] = useMutation(BATCH_UPDATE_TICKETS);

  const onBulkUpdateApi = (variables) => {
    setBulkUpdateLoading(true);
    const selectedTicketIds = getTicketsIds();
    const ticketIdentifiers = selectedTicketIds.map((ticketId) => ({
      ticketId: ticketId
    }));

    batchUpdateTickets({
      variables: {
        input: {
          ticketInput: variables,
          tickets: ticketIdentifiers
        }
      }
    })
      .then((res) => {
        setBulkUpdateLoading(false);

        if (res?.data) {
          setBulkUpdatePropModalActive(false);
          EventEmitter.dispatch('refetchClassicViewList');
          toast('success', 'app.toast.ticket.bulk.update.success');
          setSelectedRecords([]);
        }
      })
      .catch((err) => {
        setBulkUpdateLoading(false);

        setBulkUpdatePropModalActive(false);

        toast('error', 'app.toast.ticket.bulk.update.error');
      });
  };

  const getTicketsIds = (action) => {
    let filterRecords = selectedRecords;
    if (action === 'merge') {
      //Instant messaging source shouldnt be merged so filter it out for mergeTicketsIds
      filterRecords = selectedRecords.filter(
        (item) => item.source !== TICKET_SOURCE.Communication
      );
    }
    return filterRecords?.reduce(function(filtered, item) {
      switch (activeView?.systemViewProperty) {
        case 'UNKNOWN_TICKET':
        case 'TRASH_TICKET':
          filtered.push(item.ticketId);
          break;
        default:
          if (item.client !== null) {
            filtered.push(item.ticketId);
          }
          break;
      }
      return filtered;
    }, []);
  };

  return (
    <div className="fx actr p20 h90px divider white-bg">
      {activeView?.systemViewProperty === 'TRASH_TICKET' &&
      selectedRecords?.length > 0 ? (
        <></>
      ) : (
        <TicketInfoToolbar
          intl={intl}
          closeNavMenu={props.closeNavMenu}
          toggleNavMenu={props.toggleNavMenu}
          handleMouseEnter={props.handleMouseEnter}
          handleMouseLeave={props.handleMouseLeave}
          navMenu={navMenu}
          params={match.params}
          activeView={activeView}
          context={context}
          appContext={appContext}
          location={props.location}
          entitiesMeta={props.entitiesMeta}
          viewId={viewId}
          isClassicView={isClassicView}
          selectedRecordsHook={selectedRecordsHook}
          updateRecords={updateRecords}
        />
      )}
      {selectedRecords?.length > 0 && (
        <TicketActionToolbar
          intl={intl}
          params={match.params}
          activeView={activeView}
          toggleNavMenu={props.toggleNavMenu}
          navMenu={navMenu}
          context={context}
          appContext={appContext}
          location={props.location}
          setModalActive={setModalActive}
          setBulkErrorsOnClosure={setBulkErrorsOnClosure}
          viewId={viewId}
          bulkUpdatePropModalActive={bulkUpdatePropModalActive}
          setBulkUpdatePropModalActive={setBulkUpdatePropModalActive}
          isClassicView={isClassicView}
          selectedRecordsHook={selectedRecordsHook}
          updateRecords={updateRecords}
          getTicketsIds={getTicketsIds}
        />
      )}

      {bulkErrorsOnClosureByRef.current && modalActive && (
        <>
          <BulkClosureModal
            modalActive={modalActive}
            setModalActive={setModalActive}
            bulkErrorsOnClosure={bulkErrorsOnClosureByRef.current}
            setBulkErrorsOnClosure={setBulkErrorsOnClosure}
            onSave={BulkResolveTickets}
            loading={bulkUpdateLoading}
          />
        </>
      )}

      {bulkUpdatePropModalActive && (
        <BulkUpdatePropertyModal
          onBulkUpdate={onBulkUpdateApi}
          modalActive={bulkUpdatePropModalActive}
          setModalActive={setBulkUpdatePropModalActive}
          bulkUpdateMeta={bulkUpdateMeta}
          getTicketMetaForBulkUpdate={getTicketMetaForBulkUpdate}
          selectedRecords={selectedRecords}
          bulkUpdateLoading={bulkUpdateLoading}
        />
      )}
    </div>
  );
}

export default injectIntl(withRouter(withLDConsumer()(TicketToolbar)));
